import React, { FC } from 'react'
import {
  Box,
  Heading,
  Text,
  TertiaryButton,
} from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { SEARCH_NO_RESULTS_FOUND_ILLUSTRATION } from '@moonpig/web-core-brand/config'
import { useSearchStore } from '../../store/SearchStore'
import { FilterSource } from '../../types'
import { useFindLocaleText } from '../../text-localisation'
import { Filter, IntegerFilter } from '../../services/types/services'
import { FilterChip } from '../FilterChip'

const StyledNoSearchResults = styled(Box)`
  ${s({
    textAlign: 'center',
    py: 10,
    px: { xs: 6, md: 0 },
  })}
`

const StyledIllustration = styled(Box)`
  ${s({
    mx: 'auto',
    mb: 6,
    width: '240px',
    height: '180px',
  })}

  img {
    width: 100%;
    height: 100%;
  }
`

const StyledButton = styled(TertiaryButton)`
  ${s({
    typography: 'typeButtonLabel',
    color: 'colorInteractionTextLink',
    mt: 6,
    display: 'block',
    mx: 'auto',
  })}
`

const StyledChipContainer = styled(Box)`
  ${s({
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
  })}
`

export const NoSearchResults: FC<{
  noResultsFilters: (IntegerFilter | Filter)[]
}> = ({ noResultsFilters }) => {
  const clearFilters = useSearchStore(store => store.clearFilters)
  const localiseText = useFindLocaleText()

  const onClearFilters = () => {
    clearFilters({ type: 'filters', source: FilterSource.NoSearchResults })
  }

  return (
    <StyledNoSearchResults>
      <StyledIllustration
        as="img"
        src={SEARCH_NO_RESULTS_FOUND_ILLUSTRATION}
        alt={localiseText('find.no_search_results.heading')}
      />

      <Heading
        level="h3"
        mb={2}
        typography={{
          xs: 'typeMobileDisplay04',
          md: 'typeDesktopDisplay05',
        }}
      >
        {localiseText('find.no_search_results.heading')}
      </Heading>

      <Text mb={6} typography="typeBodyDefault">
        {localiseText('find.no_search_results.message')}
      </Text>

      <StyledChipContainer>
        {noResultsFilters.map(filter => (
          <FilterChip
            key={filter.id}
            filter={filter}
            select={false}
            source={FilterSource.NoSearchResults}
          />
        ))}
      </StyledChipContainer>

      <StyledButton as="button" type="button" onClick={onClearFilters}>
        {localiseText('find.no_search_results.cta_title')}
      </StyledButton>
    </StyledNoSearchResults>
  )
}
